import { AutocompleteOption, Filter, SearchField, UniversalFilterStore } from '@shared/ui';
import { RoiStore } from '../../stores';
import { ProtocolStore } from '@telespot/protocols/ui';
import { concatLatestFrom } from '@ngrx/operators';
import { Category, Option } from '@telespot/protocols/data-access';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LabelingService } from '@analysis/domain';
import { Injectable } from '@angular/core';

@Injectable()
export class FilterAutocompleteEffects {
  constructor(
    private readonly protocolStore: ProtocolStore,
    private readonly universalFilterStore: UniversalFilterStore,
    private readonly roiStore: RoiStore
  ) {}

  private readonly autocompleteLabels$ = this.protocolStore.options$.pipe(
    concatLatestFrom(() => [
      this.universalFilterStore.filters$.pipe(map((filters) => filters.filter((f) => f.field.id === 'label'))),
      this.protocolStore.categories$,
    ]),
    map(([labels, filters, categories]) => {
      if (!filters.length) return Object.values(labels).map((l) => ({ value: l.name, enabled: true }));

      const disabledNames = this.getDisabledoptionNames(filters, categories, labels);

      return Object.values(labels).map((l) => ({
        value: l.name,
        enabled: !disabledNames.has(l.name),
      }));
    })
  );

  private readonly autocompleteAssets$ = this.roiStore.assetIndex$.pipe(
    map((assetIndex) => Array.from(assetIndex.keys()).map((key) => ({ value: key, enabled: true })))
  );

  private readonly booleanFilterOptions$: Observable<AutocompleteOption[]> = of([
    { value: 'true', enabled: true },
    { value: 'false', enabled: true },
  ]);

  public readonly fieldMap: Readonly<Record<string, SearchField>> = {
    label: {
      id: 'label',
      operators: ['='],
      name: 'core.label',
      autocompleteSource: (value) => this.filterOptionByValue(this.autocompleteLabels$, value),
    },
    score: {
      id: 'score',
      operators: ['>', '<'],
      name: 'core.score',
    },
    changed: {
      id: 'changed',
      operators: ['='],
      name: 'core.changed',
      autocompleteSource: (value) => this.filterOptionByValue(this.booleanFilterOptions$, value),
    },
    unlabeled: {
      id: 'unlabeled',
      operators: ['='],
      name: 'label.unlabeled',
      autocompleteSource: (value) => this.filterOptionByValue(this.booleanFilterOptions$, value),
    },
    roiId: {
      id: 'roiId',
      operators: ['in'],
      name: 'core.id',
      formatValue: (value) => this.formatRoiIdListValue(value),
      hidden: true,
    },
    asset: {
      id: 'asset',
      operators: ['='],
      name: 'core.assettype.image',
      autocompleteSource: (value) => this.filterOptionByValue(this.autocompleteAssets$, value),
    },
  };

  public readonly searchFields: Readonly<SearchField[]> = Object.values(this.fieldMap).filter((f) => !f.hidden);

  private formatRoiIdListValue(value: string): string {
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return value;
      return `(${parsed.length})`;
    } catch {
      return value;
    }
  }

  private filterOptionByValue(obs: Observable<AutocompleteOption[]>, value: string) {
    return obs.pipe(
      map((options) =>
        !!value && value !== ''
          ? options.filter((option) => option?.value?.toLowerCase()?.includes(value?.toLowerCase()))
          : options
      )
    );
  }

  private getDisabledoptionNames(
    filters: Filter[],
    categories: Record<string, Category>,
    options: Record<string, Option>
  ): Set<string> {
    const disabled = new Set<string>();

    if (!filters.length || !categories || !options) return disabled;

    for (const f of filters) {
      const labelOption = Object.values(options).find((o) => o.name === f.value);

      if (!labelOption) continue;

      for (const optId of LabelingService.getConflictingLabels(labelOption.id, categories)) {
        const opt = options[optId];
        if (opt?.name) disabled.add(opt.name);
      }
    }

    return disabled;
  }
}
